import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/common/lib/api";
import { ezyvetClinicsQueryKey } from "@/common/lib/api/bridge";
import { useAccountContext } from "@providers/AccountProvider";
import ErrorBox from "@/common/components/ErrorBox";
import CheckCircleIcon from "@icons/check-circle.svg?react";

/**
 * EzyvetCredentialsUpload
 *
 * Sets up the ezyVet REST integration (appointment pull / consult lookup) from ezyVet's own
 * exported credentials file — the self-service replacement for a manual Supabase row insert.
 * Entirely separate from `EzyvetConnection` above it: that component saves the clinic's ezyVet
 * *web address* for the happy-bridge Playwright write-back; this one connects the REST API used
 * only to read appointments. A clinic can have either, both, or neither connected independently.
 *
 * The file is read client-side (`FileReader`, same shape as the patient-view file upload) and its
 * raw text is POSTed as-is — parsing happens server-side (`POST bridge/ezyvet-credentials`) so
 * there is exactly one parser for the file format, not two that can drift apart.
 *
 * No `Sentry.captureException` here, deliberately: `apiFetch` (`@/common/lib/api`) already reports
 * every failure itself, with curated, non-sensitive `extra` context, before this catch ever runs —
 * a second bare `captureException(err)` here would be a pure duplicate report, and `err` here is a
 * ky `HTTPError` whose own `.options`/`.request` carry the exact raw request body this upload just
 * sent (the file's `client_secret`, in full). This is not the only safeguard against that leaking,
 * though: `buildBeforeSend` (`@/common/lib/sentryUtils`) redacts any credential-shaped field
 * (`SECRET_KEY_RE` — `client_secret`, `password`, tokens, api keys) out of every event's
 * `extra`/`contexts`/breadcrumb data before it is ever sent, specifically so that a future call
 * site or Sentry config change (e.g. adding `extraErrorDataIntegration`, which would otherwise
 * start walking an Error's own enumerable properties into the event) can't silently start leaking
 * one. Avoiding the redundant call here is still worth doing on top of that — one fewer place that
 * ever hands this object to Sentry at all — but it is not the thing actually closing the leak path.
 *
 * Also caps the file at {@link MAX_FILE_SIZE_BYTES}: the real file this connects to is a few
 * hundred bytes, so anything far larger than that is very likely a wrong/accidental selection (a
 * huge binary, an unrelated document), not a bigger-than-usual credentials file.
 */
const MAX_FILE_SIZE_BYTES = 50 * 1024; // 50 KB — real ezyVet credentials files are well under 1 KB.

export default function EzyvetCredentialsUpload() {
    const { accountData } = useAccountContext();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    /** Wraps `FileReader.readAsText` in a promise — no `file.text()`, which some browsers still lack. */
    const readFileAsText = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result ?? ""));
            reader.onerror = () => reject(reader.error ?? new Error("Failed to read the selected file"));
            reader.readAsText(file);
        });

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Reset the input value so re-selecting the same file still fires onChange.
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (!file) return;

        setIsUploading(true);
        setError(null);
        setUploadedFileName(null);

        if (file.size > MAX_FILE_SIZE_BYTES) {
            setError(
                `"${file.name}" is too large (${Math.ceil(file.size / 1024)} KB) to be an ezyVet credentials file — ` +
                    `expected a small text file under ${MAX_FILE_SIZE_BYTES / 1024} KB. Check that you selected the right file.`,
            );
            setIsUploading(false);
            return;
        }

        try {
            const fileContents = await readFileAsText(file);
            await apiFetch("bridge/ezyvet-credentials", {
                method: "POST",
                json: { fileContents },
                timeout: 15_000,
            });
            setUploadedFileName(file.name);
            // The clinic picker caches a 409 ("connect credentials first") until this key is invalidated.
            await queryClient.invalidateQueries({ queryKey: ezyvetClinicsQueryKey(accountData?.id) });
        } catch (err: any) {
            // Duck-typed on `.response`, matching `EzyvetConnection`'s own error handling — a real
            // ky HTTPError always has this shape, and a network failure or abort falls through to
            // the generic message below.
            let message = "Failed to set up the ezyVet integration. Please try again or contact support.";
            const res = (err as { response?: { json: () => Promise<{ error?: string }> } })?.response;
            if (res) {
                try {
                    const body = await res.json();
                    if (body?.error) message = body.error;
                } catch {
                    // Non-JSON error body — keep the generic message.
                }
            }
            setError(message);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex flex-col gap-3">
            <div>
                <p className="text-sm font-medium text-gray-700">ezyVet API credentials</p>
                <p className="text-xs text-gray-500 mt-1">
                    Upload the credentials file ezyVet gave you when approving HappyDoc as an integration partner. This
                    connects appointment syncing — it&rsquo;s separate from the address above, which is only for signing
                    in to export Health Status records.
                </p>
            </div>
            <div>
                <button
                    type="button"
                    className="primary-button"
                    onClick={handleButtonClick}
                    disabled={isUploading}
                    aria-label="Upload ezyVet credentials file"
                >
                    {isUploading ? "Uploading…" : "Upload credentials file"}
                </button>
                <input
                    type="file"
                    accept=".txt,text/plain"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleFileChange}
                    aria-label="Select ezyVet credentials file"
                />
            </div>

            {uploadedFileName && !isUploading && !error && (
                <div className="flex items-center gap-2 text-sm text-green-700">
                    <CheckCircleIcon className="h-4 w-4 shrink-0" />
                    <span>Connected using {uploadedFileName}.</span>
                </div>
            )}

            {error && <ErrorBox msg={error} />}
        </div>
    );
}
